'use client';

import React, { forwardRef, ButtonHTMLAttributes } from 'react';

import { CustomSVGProps } from '../icons/CustomSVGProps.types';

export type IconButtonProps = Omit<CustomSVGProps, 'className' | 'style'> &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'style'> & {
        /** Icon component (not element) to display */
        icon: React.ComponentType<CustomSVGProps>;
        /** Accessibility label for screen readers */
        accessibilityLabel?: string;
        /** @deprecated Use classNameIcon instead */
        className?: string;
        /** CSS class for the icon */
        classNameIcon?: string;
        /** CSS class for the button */
        classNameButton?: string;
        /** Inline styles for the icon */
        styleIcon?: React.CSSProperties;
        /** Inline styles for the button */
        styleButton?: React.CSSProperties;
    };

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ icon: Icon, accessibilityLabel, classNameIcon, classNameButton, styleIcon, styleButton, ...props }, ref) => {
        // Separate button props from icon props
        const {
            onClick,
            disabled,
            type,
            form,
            formAction,
            formEncType,
            formMethod,
            formNoValidate,
            formTarget,
            name,
            value,
            autoFocus,
            onBlur,
            onFocus,
            onMouseDown,
            onMouseUp,
            onMouseEnter,
            onMouseLeave,
            onKeyDown,
            onKeyUp,
            tabIndex,
            ...iconProps
        } = props;

        const buttonProps = {
            onClick,
            disabled,
            type,
            form,
            formAction,
            formEncType,
            formMethod,
            formNoValidate,
            formTarget,
            name,
            value,
            autoFocus,
            onBlur,
            onFocus,
            onMouseDown,
            onMouseUp,
            onMouseEnter,
            onMouseLeave,
            onKeyDown,
            onKeyUp,
            tabIndex
        };

        return (
            <button
                ref={ref}
                aria-label={accessibilityLabel}
                className={classNameButton}
                style={styleButton}
                {...buttonProps}>
                <Icon
                    className={classNameIcon}
                    style={styleIcon}
                    {...iconProps}
                />
            </button>
        );
    }
);

IconButton.displayName = 'IconButton';

export default IconButton;
